import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Home, Compass, BookMarked, History, MessageSquare, Settings2, User, Sparkles, Workflow, Bot, FileText, Clock } from "lucide-react";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useAuth } from "@/context/AuthContext";
import { useCatalogue } from "@/context/CatalogueContext";
import { api } from "@/lib/api";
import { agentName } from "@/lib/catalogue";

const PAGES = [
  ["Home", "/", Home], ["Find an agent", "/agents", Compass], ["Prompt Hub", "/prompts", BookMarked], ["My runs", "/runs", History],
  ["Feedback", "/feedback", MessageSquare], ["What's new", "/whats-new", Sparkles], ["Profile", "/profile", User],
];
const RECENT_KEY = "fcg-palette-recent";

export const openPalette = () => window.dispatchEvent(new Event("fcg:palette"));

export const CommandPalette = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { agents, workflows } = useCatalogue();
  const [open, setOpen] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [recent, setRecent] = useState(() => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; } });

  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); } };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("fcg:palette", onOpen);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("fcg:palette", onOpen); };
  }, []);
  useEffect(() => { if (open) api.get("/prompts", { params: { sort: "upvotes" } }).then(({ data }) => setPrompts(data.slice(0, 50))).catch(() => {}); }, [open]);

  const pages = useMemo(() => (user?.role === "owner" ? [...PAGES, ["Admin", "/admin", Settings2]] : PAGES), [user]);
  const go = (item) => {
    const next = [item, ...recent.filter((r) => r.path !== item.path)].slice(0, 5);
    setRecent(next);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    setOpen(false);
    navigate(item.path);
  };
  const Item = ({ label, path, icon: Icon, hint, value }) => (
    <CommandItem value={value || `${label} ${path}`} onSelect={() => go({ label, path })} data-testid={`palette-item-${path.replace(/[^a-z0-9]+/gi, "-")}`}>
      <Icon className="mr-2 !h-4 !w-4 text-muted-foreground" aria-hidden="true" />
      <span className="truncate">{label}</span>
      {hint && <span className="ml-auto truncate pl-3 text-xs text-muted-foreground">{hint}</span>}
    </CommandItem>
  );

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search agents, workflows, prompts and pages" data-testid="palette-input" />
      <CommandList className="max-h-[420px]" data-testid="command-palette">
        <CommandEmpty>Nothing matches. Try another word.</CommandEmpty>
        {recent.length > 0 && <CommandGroup heading="Recent">{recent.map((r) => <Item key={`r-${r.path}`} label={r.label} path={r.path} icon={Clock} value={`recent ${r.label} ${r.path}`} />)}</CommandGroup>}
        <CommandGroup heading="Agents">{agents.filter((a) => !a.parent).map((a) => <Item key={a.emp_no} label={agentName(a)} path={`/agents/${a.emp_no}`} icon={Bot} hint={a.live ? "Live" : "Coming soon"} value={`${agentName(a)} ${a.function || ""} ${a.emp_no}`} />)}</CommandGroup>
        <CommandGroup heading="Workflows">{workflows.map((w) => <Item key={w.id} label={w.name} path={`/workflows/${w.id}`} icon={Workflow} hint={w.live ? "Live" : "Coming soon"} value={`${w.name} ${w.one_line || ""} ${w.id}`} />)}</CommandGroup>
        <CommandGroup heading="Prompts">{prompts.map((p) => <Item key={p.id} label={p.title} path={`/prompts?q=${encodeURIComponent(p.title)}`} icon={FileText} value={`prompt ${p.title}`} />)}</CommandGroup>
        <CommandGroup heading="Pages">{pages.map(([l, p, I]) => <Item key={p} label={l} path={p} icon={I} value={`page ${l}`} />)}</CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
